import { IconButton, Typography } from '@mui/material';
import { Stack } from '@mui/system';
import { memo, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { PreferenceIcon, RemoveIcon } from '@/assets/icons';
import { FddmServiceFactory } from '@/modules/canvas/services/Floorplan/FddmServiceFactory';
import { MultiSelectInput, getMultiSelectDisplayValue } from '@/modules/common/components/inputs';
import OptionsEditorPopover from '@modules/common/components/SelectOptionsEditorPopover';
import { PropertiesTitle } from '@/modules/common/components/PropertiesTitle';
import { useConfirm } from '@/modules/common/components/useConfirm';
import { useFloorplanDesignStore } from '@/modules/floorplanDesign/store/useFloorplanDesignStore';
import { VehicleModeRule as VehicleModeRuleType } from '../types';

type Props = {
  rules?: VehicleModeRuleType[];
  rule?: VehicleModeRuleType;
  disabled?: boolean;
  onToggle?: (vehicleTypeRef: string, modeId: string) => void;
  onRemove?: () => void;
};

function VehicleModeRuleComponent({ rules, rule, disabled = false, onToggle, onRemove }: Props) {
  const { t } = useTranslation('interface');
  const vehicleModes = useFloorplanDesignStore(
    (state) => state.uxonly.catalog.trafficRules.vehicleModes,
  );
  const allVehicles = useFloorplanDesignStore((state) => state.setup.vehicleTypes);
  const fddmSvc = useMemo(() => FddmServiceFactory.getInstance().createFddmService(), []);
  const { Dialog: ConfirmDialog, confirm } = useConfirm();

  const vehicleOptions = useMemo(
    () =>
      allVehicles
        .map((v) => ({ value: v.id, label: v.id }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [allVehicles],
  );

  const vehicleModeOptions = useMemo(
    () =>
      [...vehicleModes]
        .map((vehicleMode) => ({ value: vehicleMode, label: vehicleMode }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [vehicleModes],
  );

  const vehicleModeEntries = useMemo(
    () =>
      [...vehicleModes]
        .map((vehicleMode) => ({ id: vehicleMode, label: vehicleMode }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [vehicleModes],
  );

  const vehicleModeDisplayValue = useMemo(() => {
    const enabled = vehicleModeOptions.map((o) => ({ id: o.value, name: o.label }));
    return (ids: string[]) => getMultiSelectDisplayValue(enabled, ids);
  }, [vehicleModeOptions]);

  const hasVehicleModeOptions = vehicleModeOptions.length > 0;

  const vehicleModeRules = useMemo(() => {
    if (rules) return rules;
    return rule ? [rule] : [];
  }, [rule, rules]);

  const selectedModeIdsByVehicleId = useMemo(() => {
    const modeIdsByVehicleTypeRef = new Map<string, string[]>();

    vehicleModeRules.forEach((vehicleModeRule) => {
      const extension = vehicleModeRule.vehicleModeExtension;
      if (!extension?.vehicleTypeId) return;

      const currentModes = modeIdsByVehicleTypeRef.get(extension.vehicleTypeId) ?? [];
      const mergedModes = Array.from(new Set([...currentModes, ...extension.vehicleModes]));
      modeIdsByVehicleTypeRef.set(extension.vehicleTypeId, mergedModes);
    });

    return modeIdsByVehicleTypeRef;
  }, [vehicleModeRules]);

  const onUpdateVehicleModeOption = useCallback(
    (modeId: string, label: string) => {
      const nextMode = label.trim();
      if (!nextMode) return;

      const nextVehicleModes = Array.from(
        new Set(
          vehicleModes.map((vehicleMode) => (vehicleMode === modeId ? nextMode : vehicleMode)),
        ),
      ).sort((a, b) => a.localeCompare(b));

      fddmSvc.setCatalogTrafficRules({
        ...useFloorplanDesignStore.getState().uxonly.catalog.trafficRules,
        vehicleModes: nextVehicleModes,
      });

      const affectedLogicalSpaces = useFloorplanDesignStore
        .getState()
        .design.logicalSpaces.filter((logicalSpace) =>
          logicalSpace.trafficRules.some(
            (trafficRule) =>
              trafficRule.type === 'VEHICLE_MODE' &&
              trafficRule.vehicleModeExtension?.vehicleModes.includes(modeId),
          ),
        );

      affectedLogicalSpaces.forEach((logicalSpace) => {
        fddmSvc.updateLogicalSpace({
          ...logicalSpace,
          trafficRules: logicalSpace.trafficRules.map((trafficRule) => {
            if (
              trafficRule.type !== 'VEHICLE_MODE' ||
              !trafficRule.vehicleModeExtension?.vehicleModes.includes(modeId)
            ) {
              return trafficRule;
            }

            const nextModes = Array.from(
              new Set(
                trafficRule.vehicleModeExtension.vehicleModes.map((vehicleMode) =>
                  vehicleMode === modeId ? nextMode : vehicleMode,
                ),
              ),
            ) as [string, ...string[]];

            return {
              ...trafficRule,
              vehicleModeExtension: {
                ...trafficRule.vehicleModeExtension,
                vehicleModes: nextModes,
              },
            };
          }),
        });
      });
    },
    [fddmSvc, vehicleModes],
  );

  const onDeleteVehicleModeOption = useCallback(
    async (modeId: string) => {
      const affectedLogicalSpaces = useFloorplanDesignStore
        .getState()
        .design.logicalSpaces.filter((logicalSpace) =>
          logicalSpace.trafficRules.some(
            (trafficRule) =>
              trafficRule.type === 'VEHICLE_MODE' &&
              trafficRule.vehicleModeExtension?.vehicleModes.includes(modeId),
          ),
        );

      if (affectedLogicalSpaces.length > 0) {
        const confirmed = await confirm({
          title: t('traffic_rules.vehicle_mode_delete_used_title', 'Vehicle mode is in use'),
          message: t('traffic_rules.vehicle_mode_delete_used_message', {
            defaultValue:
              'This option is used by {{count}} logical space(s) and will be removed from them. Continue?',
            count: affectedLogicalSpaces.length,
          }),
        });

        if (!confirmed) return;
      }

      fddmSvc.setCatalogTrafficRules({
        ...useFloorplanDesignStore.getState().uxonly.catalog.trafficRules,
        vehicleModes: vehicleModes.filter((vehicleMode) => vehicleMode !== modeId),
      });

      affectedLogicalSpaces.forEach((logicalSpace) => {
        fddmSvc.updateLogicalSpace({
          ...logicalSpace,
          trafficRules: logicalSpace.trafficRules.flatMap((trafficRule) => {
            if (
              trafficRule.type !== 'VEHICLE_MODE' ||
              !trafficRule.vehicleModeExtension?.vehicleModes.includes(modeId)
            ) {
              return [trafficRule];
            }

            const remainingModes = trafficRule.vehicleModeExtension.vehicleModes.filter(
              (vehicleMode) => vehicleMode !== modeId,
            );

            if (remainingModes.length === 0) {
              return [];
            }

            return [
              {
                ...trafficRule,
                vehicleModeExtension: {
                  ...trafficRule.vehicleModeExtension,
                  vehicleModes: remainingModes as [string, ...string[]],
                },
              },
            ];
          }),
        });
      });
    },
    [confirm, fddmSvc, t, vehicleModes],
  );

  const onAddVehicleModeOption = useCallback(
    (label: string) => {
      const nextMode = label.trim();
      if (!nextMode) return;

      const alreadyExists = vehicleModes.some(
        (vehicleMode) => vehicleMode.toLowerCase() === nextMode.toLowerCase(),
      );

      if (alreadyExists) return;

      fddmSvc.setCatalogTrafficRules({
        ...useFloorplanDesignStore.getState().uxonly.catalog.trafficRules,
        vehicleModes: [...vehicleModes, nextMode].sort((a, b) => a.localeCompare(b)),
      });
    },
    [fddmSvc, vehicleModes],
  );

  return (
    <>
      <Stack
        sx={{
          flex: 1,
          minWidth: 0,
          width: '100%',
        }}
        gap={1}
      >
        <Stack
          direction='row'
          justifyContent='space-between'
          gap='12px'
          alignItems='center'
          width='100%'
        >
          <PropertiesTitle
            fontSize={14}
            fontWeight={400}
            value={t('interface:traffic_rules.vehicle_mode')}
          />

          <OptionsEditorPopover
            kind='text'
            disabled={disabled}
            ariaLabel={t('common:settings', 'Settings')}
            icon={<PreferenceIcon />}
            title={t('traffic_rules.vehicle_mode_settings', 'Vehicle mode options')}
            addButtonLabel={t('common:add', 'Add')}
            addPlaceholder={t('traffic_rules.vehicle_mode_add_placeholder', 'New vehicle mode')}
            options={vehicleModeEntries}
            onUpdateOption={onUpdateVehicleModeOption}
            onDeleteOption={onDeleteVehicleModeOption}
            onAddOption={onAddVehicleModeOption}
          />
          {onRemove && (
            <IconButton onClick={onRemove} disabled={disabled} size='small'>
              <RemoveIcon />
            </IconButton>
          )}
        </Stack>

        {vehicleOptions.length === 0 && (
          <Typography sx={{ width: '100%', opacity: 0.7 }} variant='caption'>
            {t('traffic_rules.vehicle_mode_no_vehicles', 'No vehicles available')}
          </Typography>
        )}

        {vehicleOptions.map((vehicle) => {
          const selectedIds = selectedModeIdsByVehicleId.get(vehicle.value) ?? [];

          const value = (() => {
            if (!hasVehicleModeOptions)
              return t('traffic_rules.empty_placeholder', 'No options...');
            if (selectedIds.length > 0) return vehicleModeDisplayValue(selectedIds);
            return t('common:none', 'None');
          })();

          return (
            <Stack key={vehicle.value} direction='row' alignItems='center' sx={{ width: '100%' }}>
              <Typography
                sx={{
                  flex: '1 1 40%',
                  mr: 1,
                  fontSize: '10px',
                  display: 'block',
                  minWidth: 0,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
                title={vehicle.label}
              >
                {vehicle.label}
              </Typography>

              <Stack sx={{ flex: '1 1 60%', minWidth: 0, width: '100%' }}>
                <MultiSelectInput
                  options={vehicleModeOptions}
                  selectedOptions={selectedIds}
                  onItemClick={(modeId) => onToggle?.(vehicle.value, modeId)}
                  displayValue={value}
                  disabled={disabled || !hasVehicleModeOptions || !onToggle}
                />
              </Stack>
            </Stack>
          );
        })}
      </Stack>
      <ConfirmDialog negativeBtnTextCustom={t('common:no', 'No')} />
    </>
  );
}

export const VehicleModeRule = memo(VehicleModeRuleComponent);
